import { z } from 'zod';
import { normalizeCrmCombinedSearchReturnTo } from '@features/crm-combined-search/model/return-context';
import { normalizeCrmCourseProfileNavigationReturnTo } from '@features/crm-course-profile';
import {
  crmCustomerDocumentFileStateSchema,
  crmCustomerDocumentStatusSchema,
  crmCustomerDocumentTypeSchema,
  crmDocumentEntitySortSchema,
} from '@features/crm-customer-documents';
import {
  crmCustomerLearningSectionSchema,
  crmCustomerLearningSortSchema,
} from '@features/crm-customer-learning';
import { helpdeskSearchSchema, normalizeHelpdeskDetailReturnTo } from '@features/helpdesk';
import { normalizeCrmMembershipDetailReturnTo } from '@features/crm-customer-membership';
import {
  crmCustomerDirectoryFreshnessStateSchema,
  crmCustomerDirectoryMaxPage,
  crmCustomerDirectorySearchArraySchema,
  crmCustomerDirectorySortSchema,
  userProfilesDirectorySortSchema,
  userProfilesDirectoryViewSchema,
} from '@features/crm-customer-directory';

export const crmCustomerRecordSearchSchema = z.object({
  combinedSearchReturnTo: z
    .string()
    .max(4096)
    .optional()
    .transform(normalizeCrmCombinedSearchReturnTo),
  accountLinkage: z.enum(['with', 'without']).optional(),
  ...helpdeskSearchSchema.shape,
  helpdeskReturnTo: z.string().max(4096).optional().transform(normalizeHelpdeskDetailReturnTo),
  businessLineKeys: crmCustomerDirectorySearchArraySchema(z.string().trim().min(1).max(120)),
  certificationTypeIds: crmCustomerDirectorySearchArraySchema(z.uuid()),
  cities: crmCustomerDirectorySearchArraySchema(z.string().trim().min(1).max(120)),
  currentMembershipStatuses: crmCustomerDirectorySearchArraySchema(
    z.string().trim().min(1).max(120)
  ),
  cursor: z.string().trim().min(1).max(4096).optional(),
  freshnessState: crmCustomerDirectoryFreshnessStateSchema.optional(),
  freshnessStates: crmCustomerDirectorySearchArraySchema(z.enum(['fresh', 'stale', 'unknown'])),
  linkedSystems: crmCustomerDirectorySearchArraySchema(z.enum(['bubble', 'move'])),
  mainLocationIds: crmCustomerDirectorySearchArraySchema(z.uuid()),
  membershipTypeIds: crmCustomerDirectorySearchArraySchema(z.uuid()),
  preferredLanguages: crmCustomerDirectorySearchArraySchema(z.string().trim().min(1).max(120)),
  regions: crmCustomerDirectorySearchArraySchema(z.string().trim().min(1).max(120)),
  roles: crmCustomerDirectorySearchArraySchema(z.string().trim().min(1).max(120)),
  profileOrigins: crmCustomerDirectorySearchArraySchema(z.enum(['bubble', 'move', 'bizagi'])),
  shopifyCustomerId: z.string().trim().max(200).optional(),
  shopifyId: z.string().trim().min(1).max(120).optional(),
  userOrigins: crmCustomerDirectorySearchArraySchema(z.enum(['Shopify', 'Move'])),
  view: userProfilesDirectoryViewSchema.optional(),
  membershipDateFrom: z.iso.date().optional(),
  membershipDateTo: z.iso.date().optional(),
  membershipInstructorId: z.uuid().optional(),
  membershipPage: z.coerce.number().int().positive().optional(),
  membershipQ: z.string().trim().max(100).optional(),
  membershipReturnTo: z
    .string()
    .max(4096)
    .optional()
    .transform((value) => normalizeCrmMembershipDetailReturnTo(value)),
  membershipStatus: z.string().trim().min(1).max(100).optional(),
  membershipTypeId: z.uuid().optional(),
  learningItemId: z.uuid().optional(),
  learningCertificationTypeId: z.uuid().optional(),
  learningCertifiedFrom: z.iso.date().optional(),
  learningCertifiedTo: z.iso.date().optional(),
  learningAttemptedFrom: z.iso.date().optional(),
  learningAttemptedTo: z.iso.date().optional(),
  learningCostMax: z.coerce.number().nonnegative().optional(),
  learningCostMin: z.coerce.number().nonnegative().optional(),
  learningCursor: z.string().trim().min(1).max(4096).optional(),
  learningCursorHistory: z.array(z.string().trim().min(1).max(4096)).max(100).optional(),
  learningCourseId: z.uuid().optional(),
  learningCourseStatus: z.string().trim().min(1).max(120).optional(),
  learningCourseTypeId: z.uuid().optional(),
  learningDateFrom: z.iso.date().optional(),
  learningDateTo: z.iso.date().optional(),
  learningInstructorId: z.uuid().optional(),
  learningInstructorPaymentStatus: z.string().trim().min(1).max(120).optional(),
  learningPage: z.coerce.number().int().positive().optional(),
  learningQ: z.string().trim().max(120).optional(),
  learningRegistrationId: z.uuid().optional(),
  learningRegistrationReferenceType: z.string().trim().min(1).max(120).optional(),
  learningRegistrationType: z.string().trim().min(1).max(120).optional(),
  learningResult: z.string().trim().min(1).max(120).optional(),
  learningSection: crmCustomerLearningSectionSchema.optional(),
  learningSort: crmCustomerLearningSortSchema.optional(),
  learningSchoolId: z.uuid().optional(),
  learningStudentPaymentStatus: z.string().trim().min(1).max(120).optional(),
  learningSubmittedFrom: z.iso.date().optional(),
  learningSubmittedTo: z.iso.date().optional(),
  learningType: z.string().trim().min(1).max(120).optional(),
  trainingQ: z.string().max(200).optional(),
  trainingDateStartedFrom: z.iso.date().optional(),
  trainingDateStartedTo: z.iso.date().optional(),
  trainingCursor: z.string().trim().min(1).max(4096).optional(),
  trainingCursorHistory: z.array(z.string().trim().min(1).max(4096)).max(100).optional(),
  trainingFlow: z.string().trim().min(1).max(100).optional(),
  trainingDeletionOperationId: z.uuid().optional(),
  trainingDeletedLogId: z.uuid().optional(),
  trainingRemovalOperationId: z.uuid().optional(),
  trainingRemovedProgramId: z.uuid().optional(),
  trainingProgramIds: crmCustomerDirectorySearchArraySchema(z.uuid()),
  trainingStatus: z.string().trim().min(1).max(120).optional(),
  documentFileState: crmCustomerDocumentFileStateSchema.optional(),
  documentPage: z.coerce.number().int().positive().optional(),
  documentQ: z.string().trim().max(100).optional(),
  documentRegistrationId: z.uuid().optional(),
  documentStatus: crmCustomerDocumentStatusSchema.optional(),
  documentSort: crmDocumentEntitySortSchema.optional(),
  documentType: crmCustomerDocumentTypeSchema.optional(),
  paymentQ: z.string().trim().max(200).optional(),
  page: z.coerce.number().int().positive().max(crmCustomerDirectoryMaxPage).optional(),
  q: z.string().trim().max(200).optional(),
  returnTo: z
    .string()
    .max(4096)
    .optional()
    .transform((value) => normalizeCrmCourseProfileNavigationReturnTo(value)),
  sort: z.union([crmCustomerDirectorySortSchema, userProfilesDirectorySortSchema]).optional(),
});
